import React, { useEffect, useMemo } from 'react';

import { Col, Form, Row, Typography } from 'antd';

import {
  productDetailActionTypes,
  useProductDetail,
  useProductDetailDispatch,
} from '../ProductDetailContext';
import { useScrollToFoundModule } from '../hooks';
import {
  useGetWalmartProductData,
  useGetWalmartProductSchema,
  useSaveWalmartFormValueWhenExit,
} from 'pages/branded-products/components/post-to-walmart-krogger/queries';
import { useParams } from 'react-router-dom';
import {
  useFindSchemaForProduct,
  useGetInitialWalmartFormValue,
  useScrollTopAfterRecursiveRender,
} from 'pages/branded-products/components/post-to-walmart-krogger/hooks';
import { WalmartFields } from './WalmartFields';
import { ModalEditForm } from 'pages/product-full-view/shared/components';
import { SearchProductProperties } from '../QueryProperties';
import { generateModuleProductToTree } from 'pages/product-full-view/shared/utils';
import {
  flattenAndConvert,
  formatDatesRecursive,
  removeInvalidValueKey,
} from 'pages/branded-products/components/post-to-walmart-krogger/post-product-form/utils';

import { CustomNotification } from 'common/components';
import { sleep } from 'utils/delay';
import { transformWalmartModuleToProductModule } from './utils';

import './WalmartProductEdit.less';

const WalmartProductEdit = (props) => {
  const { visible, setVisible } = props;

  const refScrollWrapper = React.useRef(null);
  const {
    advancedModule: {
      foundFieldFullPath: advancedFoundFieldFullPath,
      searchText,
    },
  } = useProductDetail();
  const { id: productId } = useParams();

  const [formInstance] = Form.useForm();

  const dispatch = useProductDetailDispatch();

  useScrollToFoundModule({
    elementByClassName: document.getElementsByClassName(
      `product-detail-view__label-form-item--${advancedFoundFieldFullPath}`
    ),
  });

  const { data: walmartSchemas } = useGetWalmartProductSchema({
    productIds: [productId],
  });

  const { walmartProductProperties } = walmartSchemas || {};

  const walmartModule = {
    moduleDescription: '',
    moduleDisplayName: 'Walmart Product Information',
    moduleName: 'walmartProductInformation',
    moduleProperties: walmartProductProperties?.[0]?.productProperties ?? [],
  };

  const transformed = transformWalmartModuleToProductModule(walmartModule);

  const { invalidateWalmartProductData } = useGetWalmartProductData({
    enabled: false,
  });

  const foundSchema = useFindSchemaForProduct({
    productId,
    walmartSchemas,
  });

  const initialWalmartForm = useGetInitialWalmartFormValue({ productId });
  const { saveWalmartFormMutation } = useSaveWalmartFormValueWhenExit();

  const walmartFormStringify = JSON.stringify(initialWalmartForm);
  const foundSchemaStringify = JSON.stringify(foundSchema);

  const productPropertySchema = foundSchema?.productProperties;
  const formattedSchema = useMemo(() => {
    return flattenAndConvert(productPropertySchema);
  }, [productPropertySchema]);

  const formattedSchemaStringify = JSON.stringify(formattedSchema);

  const onSubmit = async () => {
    const formValues = formInstance.getFieldsValue();
    const formattedUpdateFormValue = removeInvalidValueKey(formValues);

    try {
      const productMatchingData = [
        {
          data: formattedUpdateFormValue,
          needToRepublish: false,
          productId,
        },
      ];
      const response = await saveWalmartFormMutation.mutateAsync({
        productMatchingData,
      });
      const { isSuccess, message } = response || {};
      if (isSuccess) {
        CustomNotification.success('Update product detail successfully!');
        invalidateWalmartProductData();
        setVisible(false);
      } else {
        CustomNotification.error(message || 'Something went wrong');
      }
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    const fillInitialValueToForm = async () => {
      formInstance.resetFields();
      await sleep(300);
      const formattedInitFormValue = removeInvalidValueKey(initialWalmartForm);
      const formattedForm = formatDatesRecursive(
        formattedInitFormValue,
        formattedSchema
      );

      formInstance.setFieldsValue(formattedForm);
    };

    if (foundSchema && initialWalmartForm && formattedSchema) {
      fillInitialValueToForm();
    }
  }, [walmartFormStringify, foundSchemaStringify, formattedSchemaStringify]);

  useScrollTopAfterRecursiveRender(refScrollWrapper, {
    visible,
    schema: foundSchema,
  });

  return (
    <ModalEditForm
      className='product-detail-view__modal'
      title={
        <Row justify='space-between' align='middle'>
          <Col flex='auto'>
            <Typography.Text strong style={{ fontSize: 16, color: '#6c757d' }}>
              Walmart Product Information
            </Typography.Text>
          </Col>

          <Col flex='400px' style={{ marginRight: 24, marginLeft: 6 }}>
            <SearchProductProperties
              treeData={generateModuleProductToTree([transformed])}
              value={searchText}
              onChange={(searchText) =>
                dispatch({
                  type: productDetailActionTypes.SEARCH_PROPERTIES,
                  payload: {
                    searchText,
                    moduleSchema: [transformed],
                    modeView: 'edit',
                    formInstance,
                  },
                })
              }
            />
          </Col>
        </Row>
      }
      visible={visible}
      onCancel={() => {
        setVisible(false);
        dispatch({
          type: productDetailActionTypes.CLEAR_SEARCH_PROPERTIES,
        });
        dispatch({
          type: productDetailActionTypes.CLEAR_SEARCH_BASE_PROPERTIES,
        });
      }}
      onOk={onSubmit}
      okButtonProps={{
        loading: saveWalmartFormMutation.isLoading,
      }}
    >
      <div
        style={{ height: '80vh', padding: '6px 10px' }}
        className='scroller'
        ref={refScrollWrapper}
      >
        <Form
          form={formInstance}
          labelCol={{
            flex: '300px',
          }}
          wrapperCol={{
            flex: 'auto',
          }}
          className='product-detail__form'
          name='nested-messages'
        >
          {foundSchema?.['productProperties']?.map((item) => {
            const { key, ...restItemProps } = item;
            return (
              <WalmartFields
                key={item?.fullKey}
                {...restItemProps}
                fieldKey={key}
                productId={productId}
              />
            );
          })}
        </Form>
      </div>
    </ModalEditForm>
  );
};

export default WalmartProductEdit;
